import React, { useState, useEffect, useRef } from "react";
import { getCommunities, startSession, submitChoice, undoChoice, getSummary, recordVisit, resumeSession } from "../lib/api";
import WelcomeScreen from "../components/WelcomeScreen";
import InstructionsScreen from "../components/InstructionsScreen";
import CommunitySelection from "../components/CommunitySelection";
import BudgetIntro from "../components/BudgetIntro";
import ModeSelect from "../components/ModeSelect";
import EventScreen from "../components/EventScreen";
import ConsequenceScreen from "../components/ConsequenceScreen";
import JourneyEnding from "../components/JourneyEnding";
import CommunityReveal from "../components/CommunityReveal";
import DebriefScreen from "../components/DebriefScreen";
import ContributeCommunity from "../components/ContributeCommunity";
import { toast } from "sonner";
import { useLang } from "../lib/i18n";

const RESUME_KEY = "svdp_resume";
const RESUME_TTL_MS = 24 * 60 * 60 * 1000;

const saveResume = (sessionId) =>
  localStorage.setItem(RESUME_KEY, JSON.stringify({ sessionId, savedAt: Date.now() }));
const clearResume = () => localStorage.removeItem(RESUME_KEY);
const readResume = () => {
  try {
    const r = JSON.parse(localStorage.getItem(RESUME_KEY) || "null");
    if (r && r.sessionId && Date.now() - r.savedAt < RESUME_TTL_MS) return r;
  } catch (e) {}
  clearResume();
  return null;
};

export default function Simulation() {
  const { t, lang } = useLang();
  const [phase, setPhase] = useState("welcome");
  const [loading, setLoading] = useState(false);
  const [communities, setCommunities] = useState([]);
  const [session, setSession] = useState(null);
  const [state, setState] = useState(null);
  const [event, setEvent] = useState(null);
  const [result, setResult] = useState(null);
  const [summary, setSummary] = useState(null);
  const [prevPhase, setPrevPhase] = useState("welcome");
  const [resumeInfo, setResumeInfo] = useState(null);
  const [mode, setMode] = useState("deep");
  const [communityId, setCommunityId] = useState(null);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  useEffect(() => {
    recordVisit();
    setResumeInfo(readResume());
  }, []);

  // Re-fetch localized backend content when the language is switched mid-session
  // so a screen never shows mixed English/Spanish content.
  const langRef = useRef(lang);
  useEffect(() => {
    if (langRef.current === lang) return;
    langRef.current = lang;
    if (!session) return;
    (async () => {
      try {
        if (["budget", "event", "consequence"].includes(phase)) {
          // Only the text is language-dependent; keep existing numeric state as-is.
          const data = await resumeSession(session.id);
          setSession((s) => ({ ...s, community_name: data.community_name, family: data.family }));
          if (data.event) setEvent(data.event);
        }
        if (["ending", "reveal", "debrief"].includes(phase)) {
          const s = await getSummary(session.id);
          setSummary(s);
        }
      } catch (e) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const resume = async () => {
    const r = readResume();
    if (!r) return;
    setLoading(true);
    try {
      const data = await resumeSession(r.sessionId);
      if (data.completed || !data.event) {
        clearResume();
        setResumeInfo(null);
        toast.info(t("That walk-through was already finished — starting fresh."));
        return;
      }
      setSession(data);
      setState(data.state);
      setEvent(data.event);
      setPhase("event");
      scrollTop();
    } catch (e) {
      clearResume();
      setResumeInfo(null);
      toast.error(t("We couldn't find that walk-through. Please start again."));
    } finally {
      setLoading(false);
    }
  };

  const beginSelection = async () => {
    setLoading(true);
    try {
      const data = await getCommunities();
      setCommunities(data);
      setPhase("community");
      scrollTop();
    } catch (e) {
      toast.error(t("Could not load communities. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const selectCommunity = async (communityId) => {
    setLoading(true);
    try {
      const data = await startSession(communityId, mode);
      setCommunityId(communityId);
      setSession(data);
      saveResume(data.id);
      setResumeInfo(null);
      setState({
        money_left_this_month: data.situation.discretionary,
        income_this_month: data.situation.income,
        essentials: data.situation.essentials,
        indicators: data.indicators,
      });
      setEvent(data.event);
      setPhase("budget");
      scrollTop();
    } catch (e) {
      toast.error(t("Could not start the experience. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const startDeepDive = async () => {
    setLoading(true);
    try {
      const data = await startSession(communityId, "deep", true);
      setMode("deep");
      setSession(data);
      saveResume(data.id);
      setResult(null);
      setSummary(null);
      setState({
        money_left_this_month: data.situation.discretionary,
        income_this_month: data.situation.income,
        essentials: data.situation.essentials,
        indicators: data.indicators,
      });
      setEvent(data.event);
      setPhase("budget");
      scrollTop();
    } catch (e) {
      toast.error(t("Could not start the full year. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const endingContinue = () => {
    if (mode === "quick") {
      startDeepDive();
    } else {
      setPhase("reveal");
      scrollTop();
    }
  };

  const handleChoice = async (choiceId, reflection) => {
    setLoading(true);
    try {
      const res = await submitChoice(session.id, choiceId, reflection);
      setResult(res);
      if (res.completed) clearResume();
      setState({
        money_left_this_month: res.money_left_this_month,
        income_this_month: res.income_this_month,
        essentials: res.essentials,
        indicators: res.indicators,
      });
      setPhase("consequence");
      scrollTop();
    } catch (e) {
      toast.error(t("Something went wrong recording your choice."));
    } finally {
      setLoading(false);
    }
  };

  const reconsider = async () => {
    setLoading(true);
    try {
      const res = await undoChoice(session.id);
      setState(res.state);
      setEvent(res.event);
      setPhase("event");
      scrollTop();
    } catch (e) {
      toast.error(t("Could not go back. Please continue."));
    } finally {
      setLoading(false);
    }
  };

  const continueFromConsequence = async () => {
    if (result.completed) {
      try {
        const s = await getSummary(session.id);
        setSummary(s);
        setPhase("ending");
        scrollTop();
      } catch (e) {
        toast.error(t("Could not load the summary."));
      }
    } else {
      setEvent(result.event);
      setPhase("event");
      scrollTop();
    }
  };

  const restart = () => {
    clearResume();
    setResumeInfo(null);
    setSession(null);
    setState(null);
    setEvent(null);
    setResult(null);
    setSummary(null);
    setPhase("welcome");
    scrollTop();
  };

  const goContribute = (from) => {
    setPrevPhase(from);
    setPhase("contribute");
    scrollTop();
  };

  if (phase === "welcome")
    return <WelcomeScreen onBegin={() => { setPhase("mode"); scrollTop(); }} loading={loading} onContribute={() => goContribute("welcome")} resumeAvailable={!!resumeInfo} onResume={resume} />;
  if (phase === "mode")
    return <ModeSelect onSelect={(m) => { setMode(m); setPhase("instructions"); scrollTop(); }} />;
  if (phase === "instructions")
    return <InstructionsScreen onContinue={beginSelection} quick={mode === "quick"} />;
  if (phase === "community")
    return <CommunitySelection communities={communities} onSelect={selectCommunity} selecting={loading} quick={mode === "quick"} />;
  if (phase === "budget") return <BudgetIntro session={session} onBegin={() => { setPhase("event"); scrollTop(); }} quick={mode === "quick"} />;
  if (phase === "event")
    return (
      <EventScreen
        event={event}
        state={state}
        communityName={session.community_name}
        onSubmit={handleChoice}
        submitting={loading}
        quick={mode === "quick"}
      />
    );
  if (phase === "consequence")
    return <ConsequenceScreen result={result} communityName={session.community_name} onContinue={continueFromConsequence} onReconsider={reconsider} reconsidering={loading} quick={mode === "quick"} />;
  if (phase === "ending")
    return <JourneyEnding summary={summary} quick={mode === "quick"} onContinue={endingContinue} deepLoading={loading} />;
  if (phase === "reveal")
    return (
      <CommunityReveal
        summary={summary}
        sessionId={session.id}
        onContinue={() => { setPhase("debrief"); scrollTop(); }}
      />
    );
  if (phase === "debrief")
    return <DebriefScreen summary={summary} onRestart={restart} onContribute={() => goContribute("debrief")} />;
  if (phase === "contribute")
    return <ContributeCommunity onBack={() => { setPhase(prevPhase); scrollTop(); }} onHome={restart} />;
  return null;
}
